import { createContext, ReactNode, use, useEffect, useState } from 'react';
import { DateString, todayDate } from '../../../common/src/date_string.ts';

export const CurrentDateContext = createContext<DateString | undefined>(undefined);

export function useCurrentDate(): DateString {
  return use(CurrentDateContext) ?? todayDate();
}

export const CurrentDateProvider = ({ children }: { children: ReactNode }) => {
  const [liveDate, setLiveDate] = useState<DateString>(() => todayDate());

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextUpdate = () => {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      timeoutId = setTimeout(() => {
        setLiveDate(todayDate());
        scheduleNextUpdate();
      }, nextMidnight.getTime() - now.getTime());
    };

    // setTimeout is unreliable across tab suspension (especially on mobile); re-check on wake.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      setLiveDate(todayDate());
      clearTimeout(timeoutId);
      scheduleNextUpdate();
    };

    scheduleNextUpdate();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return <CurrentDateContext value={liveDate}>{children}</CurrentDateContext>;
};
